import { useParams, Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useState } from "react";
import { ArrowLeft, RotateCw, ShieldOff, Mail, Shield, CheckCircle, Circle, Clock } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogClose } from "@/components/ui/dialog";

const VALID_TRANSITIONS = { PENDING: ["ACTIVE"], ACTIVE: ["SUSPENDED", "EXPIRED"], SUSPENDED: ["ACTIVE", "EXPIRED"], EXPIRED: ["ACTIVE"] };
const TRANSITION_LABELS = { ACTIVE: "Activate membership", SUSPENDED: "Suspend membership", EXPIRED: "Expire membership" };
const statusColor = (s) => {
  if (s === "ACTIVE") return "text-lv-active bg-lv-active/10 border-lv-active/20";
  if (s === "SUSPENDED" || s === "EXPIRED") return "text-lv-inactive bg-lv-inactive/10 border-lv-inactive/20";
  return "text-bronze bg-bronze/10 border-bronze/20";
};

function JourneyTimeline({ journey }) {
  if (!journey?.length) return <p className="text-sm text-lv-muted">No events recorded</p>;
  return (
    <div className="space-y-0">
      {journey.map((evt, i) => (
        <div key={i} className="flex items-start gap-3 pb-4 relative">
          {i < journey.length - 1 && <div className="absolute left-[9px] top-[22px] bottom-0 w-px bg-lv-border" />}
          <div className="mt-1 shrink-0"><CheckCircle size={18} className="text-lv-active" /></div>
          <div className="min-w-0 flex-1">
            <p className="text-sm text-cream">{evt.event}</p>
            {evt.detail && <p className="text-xs text-lv-muted mt-0.5">{evt.detail}</p>}
            <p className="text-[10px] text-lv-muted/50 mt-0.5">{evt.timestamp ? new Date(evt.timestamp).toLocaleString() : ""}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

export default function MemberDetail() {
  const { id } = useParams();
  const qc = useQueryClient();
  const [showTransition, setShowTransition] = useState(false);
  const [targetStatus, setTargetStatus] = useState("");
  const [reason, setReason] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const { data: member, isLoading, error } = useQuery({ queryKey: ["admin-member", id], queryFn: () => api.get(`/admin/members/${id}`) });
  const invalidate = () => { qc.invalidateQueries({ queryKey: ["admin-member", id] }); qc.invalidateQueries({ queryKey: ["admin-members"] }); };
  const doAction = async (fn) => { setActionLoading(true); try { await fn(); invalidate(); } catch (err) { toast.error(err.message); } finally { setActionLoading(false); } };

  if (isLoading) return <div className="py-12 text-center"><div className="w-10 h-0.5 bg-bronze mx-auto animate-pulse" /></div>;
  if (error || !member) return <div className="py-12 text-center text-lv-muted">Member not found.</div>;

  const transitions = VALID_TRANSITIONS[member.status] || [];

  return (
    <div data-testid="admin-member-detail">
      <Link to="/admin/members" className="inline-flex items-center gap-2 text-sm text-lv-muted hover:text-cream transition-colors mb-6 min-h-[44px]"><ArrowLeft size={16} /> Members</Link>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="font-serif text-2xl sm:text-3xl text-cream tracking-wide" data-testid="member-detail-name">{member.full_name}</h1>
          <p className="font-mono text-sm text-lv-muted mt-1" data-testid="member-detail-code">{member.member_code}</p>
        </div>
        <span className={`inline-flex items-center px-3 py-1.5 text-[10px] tracking-[0.3em] uppercase rounded-full border ${statusColor(member.status)}`} data-testid="member-detail-status">{member.status}</span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 sm:gap-6">
        <div className="lg:col-span-2 space-y-5">
          {/* Member Info */}
          <div className="bg-walnut border border-lv-border p-5 sm:p-6">
            <h2 className="text-xs tracking-[0.3em] uppercase text-lv-muted mb-5">Member Information</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div><p className="text-[10px] tracking-[0.2em] uppercase text-lv-muted">Full Name</p><p className="text-sm text-cream mt-0.5 break-words">{member.full_name}</p></div>
              {member.preferred_name && <div><p className="text-[10px] tracking-[0.2em] uppercase text-lv-muted">Preferred Name</p><p className="text-sm text-cream mt-0.5">{member.preferred_name}</p></div>}
              <div><p className="text-[10px] tracking-[0.2em] uppercase text-lv-muted">Chapter</p><p className="text-sm text-cream mt-0.5">{member.home_chapter_name || "\u2014"}</p></div>
              <div><p className="text-[10px] tracking-[0.2em] uppercase text-lv-muted">Member Since</p><p className="text-sm text-cream mt-0.5">{member.member_since ? new Date(member.member_since).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }) : "\u2014"}</p></div>
              <div><p className="text-[10px] tracking-[0.2em] uppercase text-lv-muted">Email</p><p className="text-sm text-cream mt-0.5 break-all">{member.user?.email || "\u2014"}</p></div>
              <div><p className="text-[10px] tracking-[0.2em] uppercase text-lv-muted">Account Verified</p><p className="text-sm text-cream mt-0.5">{member.user?.is_verified ? "Yes" : "No"}</p></div>
              {member.is_founding_member && <div className="sm:col-span-2"><span className="inline-flex items-center px-3 py-1 text-[10px] tracking-[0.3em] uppercase rounded-full bg-bronze/10 text-bronze border border-bronze/20">Founding Member</span></div>}
            </div>
          </div>

          {/* Membership Journey */}
          <div className="bg-walnut border border-lv-border p-5 sm:p-6">
            <h2 className="text-xs tracking-[0.3em] uppercase text-lv-muted mb-5">Membership Journey</h2>
            <JourneyTimeline journey={member.journey} />
          </div>
        </div>

        {/* Actions sidebar */}
        <div className="space-y-4">
          {transitions.length > 0 && (
            <div className="bg-walnut border border-lv-border p-4 sm:p-5">
              <h3 className="text-xs tracking-[0.3em] uppercase text-lv-muted mb-4">Membership Actions</h3>
              <div className="space-y-2">
                {transitions.map((s) => (
                  <button key={s} onClick={() => { setTargetStatus(s); setShowTransition(true); }} className="lv-btn-secondary w-full text-left text-xs min-h-[44px]" data-testid={`transition-to-${s.toLowerCase()}`}>
                    {TRANSITION_LABELS[s] || `Transition to ${s}`}
                  </button>
                ))}
              </div>
            </div>
          )}

          {member.status === "PENDING" && !member.user?.is_verified && (
            <button onClick={() => doAction(async () => { const d = await api.post(`/admin/members/${id}/resend-invite`); toast.success("Invitation resent"); if (d.claim_link) toast.info(`Link: ${d.claim_link}`, { duration: 12000 }); })} disabled={actionLoading} className="lv-btn-secondary w-full inline-flex items-center justify-center gap-2 min-h-[44px]" data-testid="resend-invite-btn">
              <Mail size={16} /> Resend Invitation
            </button>
          )}

          {/* Advanced: Credential Management */}
          <div className="bg-walnut border border-lv-border p-4 sm:p-5">
            <button onClick={() => setShowAdvanced(!showAdvanced)} className="text-xs tracking-[0.3em] uppercase text-lv-muted hover:text-cream transition-colors w-full text-left">
              {showAdvanced ? "\u25B4" : "\u25BE"} Credential Management
            </button>
            {showAdvanced && (
              <div className="mt-4 space-y-3">
                {member.has_credential_token ? (
                  <>
                    <div className="flex items-center gap-2"><Shield size={14} className="text-lv-active" /><span className="text-xs text-lv-active tracking-wider uppercase">Active credential</span></div>
                    <p className="text-[10px] text-lv-muted">Issued {member.credential_token_created ? new Date(member.credential_token_created).toLocaleDateString() : "\u2014"}</p>
                    <button onClick={() => doAction(async () => { await api.post(`/admin/members/${id}/rotate-token`); toast.success("Credential rotated"); })} disabled={actionLoading} className="lv-btn-secondary w-full inline-flex items-center justify-center gap-2 text-xs min-h-[44px]" data-testid="rotate-token-btn"><RotateCw size={14} /> Rotate Credential</button>
                    <button onClick={() => doAction(async () => { await api.post(`/admin/members/${id}/revoke-token`); toast.success("Credential revoked"); })} disabled={actionLoading} className="lv-btn-secondary w-full inline-flex items-center justify-center gap-2 text-xs min-h-[44px] border-lv-inactive/30 text-lv-inactive hover:border-lv-inactive" data-testid="revoke-token-btn"><ShieldOff size={14} /> Revoke Credential</button>
                  </>
                ) : (
                  <p className="text-sm text-lv-muted leading-relaxed">No active credential. Issued automatically when membership is activated.</p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <Dialog open={showTransition} onOpenChange={setShowTransition}>
        <DialogContent className="bg-walnut-deep border-lv-border text-cream max-w-[90vw] sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl text-cream">{TRANSITION_LABELS[targetStatus] || targetStatus}</DialogTitle>
            <DialogDescription className="text-lv-muted">{member.full_name} &mdash; {member.status} &rarr; {targetStatus}</DialogDescription>
          </DialogHeader>
          <div className="py-2"><label className="block text-xs tracking-[0.2em] uppercase text-lv-muted mb-2">Reason (optional)</label><input value={reason} onChange={(e) => setReason(e.target.value)} className="lv-input" placeholder="Reason for change" data-testid="transition-reason" /></div>
          <DialogFooter className="flex gap-2">
            <DialogClose asChild><button className="lv-btn-secondary min-h-[44px]">Cancel</button></DialogClose>
            <button onClick={() => doAction(async () => { await api.patch(`/admin/members/${id}/status`, { to_status: targetStatus, reason: reason || null }); toast.success(`Membership ${targetStatus === "ACTIVE" ? "activated" : targetStatus.toLowerCase()}`); setShowTransition(false); setTargetStatus(""); setReason(""); })} disabled={actionLoading} className="lv-btn-primary min-h-[44px]" data-testid="confirm-transition">{actionLoading ? "Processing\u2026" : "Confirm"}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
